import React, { useContext, useEffect } from 'react';
import { useQuery } from 'react-query';

import AdditionalResources from 'lib/AdditionalResources/AdditionalResources';
import PollTeaserList from 'lib/PollTeaserList/PollTeaserList';
import formatDate from 'utils/formatDate';
import ApiContext from 'api/ApiContext';
import { parseDateString } from 'utils/getDateFromPath';
import { PollType } from 'api/api.types';
import { Contentheader, Pagesection } from '@ktag/styleguide/dist/react';
import BreadcrumbsContext from 'lib/WAPBreadcrumbs/BreadcrumbsContext';
import BackButton from 'lib/BackButton/BackButton';
import { useTranslation } from 'react-i18next';
import NotFoundMessage from 'lib/ErrorMessage/NotFoundMessage';
import WAPBreadcrumbs from 'lib/WAPBreadcrumbs/WAPBreadcrumbs';
import WAPLeadtext from 'lib/WAPLeadtext/WAPLeadtext';

interface Props {
  id: string;
  preventBreadcrumbUpdate?: boolean;
  headingLevel?: 1 | 2;
}

const PollPage = ({ id, preventBreadcrumbUpdate, headingLevel }: Props) => {
  const { getPollDay, language } = useContext(ApiContext);
  const query = useQuery(['pollday', id], () => getPollDay(id));
  const { setBreadcrumbItems } = useContext(BreadcrumbsContext);
  const { t } = useTranslation('wap');

  const date = parseDateString(query.data?.data?.date);
  const formattedDate = formatDate(date, { month: 'long', day: 'numeric' });

  useEffect(() => {
    if (preventBreadcrumbUpdate || !formattedDate) return;

    const breadcrumbItems = [
      {
        title: formattedDate,
        url: '',
      },
    ];

    setBreadcrumbItems(breadcrumbItems);
  }, [formattedDate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (query.status === 'error') return <NotFoundMessage type="pollday" />;

  const createTeaserList = (types: PollType[]) => {
    return (
      query.data?.data?.issues.filter((issue) => types.includes(issue.info.type)) ?? []
    );
  };

  const pollList = createTeaserList(['poll_simple', 'poll_complex']);
  const electionList = createTeaserList([
    'election_majority_system',
    'election_proportional_apportionment',
    'election_biproportional_apportionment',
  ]);

  const info = query.data?.data?.info?.[language];

  const isPolldayEmpty = !query?.data?.data?.issues?.length && query.isLoading === false;

  return (
    <>
      <Pagesection>
        {headingLevel === 1 ? (
          <Contentheader
            beforeBreadcrumbs={<WAPBreadcrumbs />}
            beforeBacklink={<BackButton label={t('backbutton_home')} />}
            pagetitle={formattedDate}
            beforeLeadtext={<WAPLeadtext leadtext={info} />}
            printing={!query.isLoading}
          />
        ) : (
          <div className="richtext">
            {date && <h2 className="pollpage__date">{formattedDate}</h2>}
            {info && <WAPLeadtext leadtext={info} />}
          </div>
        )}
      </Pagesection>
      {Boolean(pollList?.length) && (
        <PollTeaserList
          issues={pollList}
          type="POLL"
          headingLevel={(headingLevel + 1) as 2 | 3}
        />
      )}
      {Boolean(electionList?.length) && (
        <PollTeaserList
          issues={electionList}
          type="ELECTION"
          headingLevel={(headingLevel + 1) as 2 | 3}
        />
      )}
      {isPolldayEmpty && (
        <Pagesection>
          <p>{t('pollpage_no_issues')}</p>
        </Pagesection>
      )}
      <Pagesection type="slender">
        <AdditionalResources
          id={id}
          hasDownloads={query.data?.data.resources?.downloads}
          links={query.data?.data.links}
          issues={query.data?.data?.issues}
          headingLevel={(headingLevel + 1) as 2 | 3}
        />
      </Pagesection>
    </>
  );
};

PollPage.defaultProps = {
  headingLevel: 1,
};

export default PollPage;
